import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '@/lib/api-client';

export interface CommonProductImage {
  id: string;
  name: string;
  keywords: string[];
  imageUrl: string;
  department: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useCommonProductImages() {
  return useQuery({
    queryKey: ['admin', 'common-product-images'],
    queryFn: () => productsApi.get<{ data: CommonProductImage[] }>('/admin/common-product-images'),
  });
}

/** A common image resolved by the products-service matcher. */
export interface MatchedCommonProductImage {
  id: string;
  name: string;
  keywords: string[];
  imageUrl: string;
  department: string | null;
}

/** Resolve the generic image for a product via the products service, which
 *  matches on department + name + keywords (whole-word, strict-scoped). Matching
 *  lives in the products service — not the dashboard — so the dashboard preview
 *  and the POS register always agree. Returns the matched image, or null. */
export function useMatchedCommonProductImage(
  productName: string | undefined | null,
  department: string | undefined | null,
  enabled = true,
) {
  const name = productName?.trim() ?? '';
  const dept = department?.trim() ?? '';
  return useQuery({
    queryKey: ['common-product-images', 'match', name, dept],
    queryFn: () => {
      const params = new URLSearchParams({ name });
      if (dept) params.set('department', dept);
      return productsApi.get<{ data: MatchedCommonProductImage | null }>(
        `/admin/common-product-images/match?${params.toString()}`,
      );
    },
    enabled: enabled && name.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCommonProductImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      file: File;
      name: string;
      keywords: string;
      department?: string;
      sortOrder?: number;
    }) => {
      const formData = new FormData();
      formData.append('file', payload.file);
      formData.append('name', payload.name);
      formData.append('keywords', payload.keywords);
      if (payload.department) formData.append('department', payload.department);
      if (payload.sortOrder !== undefined) formData.append('sortOrder', String(payload.sortOrder));
      const response = await fetch('/products-api/v1/admin/common-product-images', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
        throw error;
      }
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'common-product-images'] });
    },
  });
}

export function useUpdateCommonProductImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string;
      file?: File;
      name?: string;
      keywords?: string;
      department?: string;
      sortOrder?: number;
      isActive?: boolean;
    }) => {
      const formData = new FormData();
      if (payload.file) formData.append('file', payload.file);
      if (payload.name !== undefined) formData.append('name', payload.name);
      if (payload.keywords !== undefined) formData.append('keywords', payload.keywords);
      if (payload.department !== undefined) formData.append('department', payload.department);
      if (payload.sortOrder !== undefined) formData.append('sortOrder', String(payload.sortOrder));
      if (payload.isActive !== undefined) formData.append('isActive', String(payload.isActive));
      const response = await fetch(`/products-api/v1/admin/common-product-images/${payload.id}`, {
        method: 'PUT',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: 'Update failed' } }));
        throw error;
      }
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'common-product-images'] });
    },
  });
}

export function useDeleteCommonProductImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/products-api/v1/admin/common-product-images/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok && response.status !== 204) {
        const error = await response.json().catch(() => ({ error: { message: 'Delete failed' } }));
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'common-product-images'] });
    },
  });
}
